// Admin overview — counts + quick links.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../shared/api/client";
import { I } from "../../../shared/Icon";
import { useAuth } from "../../../shared/stores/auth";
import { toast } from "../../../shared/stores/toasts";

export function AdminOverview() {
  const [counts, setCounts] = useState({ users: 0, audits: 0, backups: 0 });
  const [restarting, setRestarting] = useState(false);
  const hasRole = useAuth((s) => s.hasRole);
  const nav = useNavigate();

  const onRestart = async () => {
    if (!window.confirm(
      "전체 timbox 서버(스택)를 재시작합니다.\n\n· 1~2분간 접속이 끊길 수 있습니다.\n· 진행 중인 작업이 중단될 수 있습니다.\n\n계속하시겠습니까?"
    )) return;
    setRestarting(true);
    try {
      await api.post("/admin/system/restart");
      toast.success("서버 재시작 요청됨", "1~2분 후 자동 복구됩니다. 잠시 뒤 페이지를 새로고침하세요.");
    } catch (e: any) {
      toast.error("재시작 실패", e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? "");
      setRestarting(false);
    }
  };
  useEffect(() => {
    Promise.allSettled([
      api.get<{ meta: { total: number } }>("/users?size=1").then((r) => r.data.meta.total),
      api.get<{ meta: { total: number } }>("/admin/audit-logs?size=1").then((r) => r.data.meta.total),
      api.get<unknown[]>("/admin/backups").then((r) => r.data.length),
    ]).then(([u, a, b]) => {
      setCounts({
        users: u.status === "fulfilled" ? u.value : 0,
        audits: a.status === "fulfilled" ? a.value : 0,
        backups: b.status === "fulfilled" ? b.value : 0,
      });
    });
  }, []);

  return (
    <>
      <div className="t-grid cols-3">
        <Card icon={<I.Users size={18}/>} label="총 사용자" value={counts.users} onClick={() => nav("/admin?filter=users")}/>
        <Card icon={<I.Shield size={18}/>} label="감사 로그" value={counts.audits} onClick={() => nav("/admin?filter=audit")}/>
        <Card icon={<I.Cloud size={18}/>} label="백업 이력" value={counts.backups} onClick={() => nav("/admin?filter=backup")}/>
      </div>

      {hasRole("SUPER_ADMIN") && (
        <div className="t-card" style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>서버 재시작</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2, lineHeight: 1.5 }}>
              timbox 스택 전체(API·워커·프론트·DB·캐시 등)를 Docker로 재시작합니다. 1~2분간 접속이 끊길 수 있습니다.
            </div>
          </div>
          <button
            onClick={onRestart}
            disabled={restarting}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "1px solid #dc2626",
              background: restarting ? "#fca5a5" : "#dc2626", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: restarting ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}
          >
            <I.Settings size={14}/> {restarting ? "재시작 요청 중…" : "서버 재시작"}
          </button>
        </div>
      )}
    </>
  );
}

function Card({ icon, label, value, onClick }: { icon: React.ReactNode; label: string; value: number; onClick: () => void }) {
  return (
    <div className="t-card" onClick={onClick} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--p-tint)", color: "var(--p-700)", display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
        <div>
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>{label}</div>
          <div className="num" style={{ fontSize: 24, fontWeight: 700 }}>{value.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}
